/**
 * Cliente HTTP del **portal del cliente** (`/api/portal/...`).
 *
 * Va contra el mismo EDSuite API, pero con el token del portal en lugar del token
 * del panel:
 * - `auth: false` evita que `api-client` inyecte el token del operador.
 * - `retryOnAuthError: false` desactiva el refresco del panel: el portal no tiene
 *   refresh token, así que un 401 significa «vuelve a entrar» y el guard del
 *   portal se encarga de limpiar la sesión.
 *
 * El token viaja por cabecera (`Authorization: Bearer`) exactamente igual que el
 * del panel; el `scope` lo distingue en el servidor.
 */

import { apiClient } from '@/lib/http/api-client';

import { usePortalStore } from './portal.store';
import type {
  BalancePortal,
  ClientePortal,
  ConductorFormulario,
  ConductorPortal,
  ConsumoPortal,
  DashboardPortal,
  DespachoEnVivoPortal,
  DespachoPortal,
  FacturaDetallePortal,
  FacturaPendientePortal,
  FacturaPortal,
  LineaCreditoPortal,
  MensajeFormulario,
  MensajePortal,
  MovimientoPortal,
  SesionPortalRespuesta,
  VehiculoFormulario,
  VehiculoPortal,
} from './portal.types';

/** Cabecera con el token del portal (vacía si no hay sesión). */
function cabeceras(): Record<string, string> {
  const token = usePortalStore.getState().token;

  return token ? { Authorization: `Bearer ${token}` } : {};
}

/** Opciones comunes de toda petición del portal. */
function opciones(): { auth: false; retryOnAuthError: false; headers: Record<string, string> } {
  return { auth: false, retryOnAuthError: false, headers: cabeceras() };
}

export const portalApi = {
  iniciarSesion: (numeroDoc: string, password: string): Promise<SesionPortalRespuesta> =>
    apiClient.post<SesionPortalRespuesta>('/api/portal/login', {
      auth: false,
      retryOnAuthError: false,
      body: { numero_doc: numeroDoc, password },
    }),

  cerrarSesion: (): Promise<{ mensaje: string }> =>
    apiClient.post<{ mensaje: string }>('/api/portal/logout', opciones()),

  miCuenta: (signal?: AbortSignal): Promise<{ cliente: ClientePortal }> =>
    apiClient.get<{ cliente: ClientePortal }>('/api/portal/me', { ...opciones(), signal }),

  dashboard: (signal?: AbortSignal): Promise<DashboardPortal> =>
    apiClient.get<DashboardPortal>('/api/portal/dashboard', { ...opciones(), signal }),

  facturas: (
    filtros: { desde?: string; hasta?: string; serie?: string; numero?: string; placa?: string; origen?: string },
    signal?: AbortSignal,
  ): Promise<{ total: number; playa: number; administrativas: number; facturas: FacturaPortal[] }> =>
    apiClient.get<{ total: number; playa: number; administrativas: number; facturas: FacturaPortal[] }>(
      '/api/portal/facturas',
      { ...opciones(), query: filtros, signal },
    ),

  facturaDetalle: (id: number, signal?: AbortSignal): Promise<{ factura: FacturaDetallePortal }> =>
    apiClient.get<{ factura: FacturaDetallePortal }>(`/api/portal/facturas/${id}`, {
      ...opciones(),
      signal,
    }),

  consumos: (
    filtros: { desde?: string; hasta?: string },
    signal?: AbortSignal,
  ): Promise<{ total: number; total_monto: number; consumos: ConsumoPortal[] }> =>
    apiClient.get<{ total: number; total_monto: number; consumos: ConsumoPortal[] }>(
      '/api/portal/consumos',
      { ...opciones(), query: filtros, signal },
    ),

  creditos: (
    signal?: AbortSignal,
  ): Promise<{ lineas: LineaCreditoPortal[]; facturas_pendientes: FacturaPendientePortal[] }> =>
    apiClient.get<{ lineas: LineaCreditoPortal[]; facturas_pendientes: FacturaPendientePortal[] }>(
      '/api/portal/creditos',
      { ...opciones(), signal },
    ),

  balance: (filtros: { desde?: string; hasta?: string }, signal?: AbortSignal): Promise<BalancePortal> =>
    apiClient.get<BalancePortal>('/api/portal/pagos/balance', {
      ...opciones(),
      query: filtros,
      signal,
    }),

  movimientos: (
    filtros: { desde?: string; hasta?: string },
    signal?: AbortSignal,
  ): Promise<{ total: number; movimientos: MovimientoPortal[] }> =>
    apiClient.get<{ total: number; movimientos: MovimientoPortal[] }>(
      '/api/portal/pagos/movimientos',
      { ...opciones(), query: filtros, signal },
    ),

  /* ---------------------------------------------------------------- *
   * Vehículos y conductores
   * ---------------------------------------------------------------- */

  vehiculos: (signal?: AbortSignal): Promise<{ vehiculos: VehiculoPortal[] }> =>
    apiClient.get<{ vehiculos: VehiculoPortal[] }>('/api/portal/vehiculos', { ...opciones(), signal }),

  crearVehiculo: (datos: VehiculoFormulario): Promise<{ id: number; placa: string }> =>
    apiClient.post<{ id: number; placa: string }>('/api/portal/vehiculos', {
      ...opciones(),
      body: datos,
    }),

  actualizarVehiculo: (id: number, datos: VehiculoFormulario): Promise<{ id: number; placa: string }> =>
    apiClient.put<{ id: number; placa: string }>(`/api/portal/vehiculos/${id}`, {
      ...opciones(),
      body: datos,
    }),

  inhabilitarVehiculo: (id: number): Promise<{ id: number }> =>
    apiClient.delete<{ id: number }>(`/api/portal/vehiculos/${id}`, opciones()),

  conductores: (signal?: AbortSignal): Promise<{ conductores: ConductorPortal[] }> =>
    apiClient.get<{ conductores: ConductorPortal[] }>('/api/portal/conductores', {
      ...opciones(),
      signal,
    }),

  crearConductor: (datos: ConductorFormulario): Promise<{ id: number }> =>
    apiClient.post<{ id: number }>('/api/portal/conductores', { ...opciones(), body: datos }),

  actualizarConductor: (id: number, datos: ConductorFormulario): Promise<{ id: number }> =>
    apiClient.put<{ id: number }>(`/api/portal/conductores/${id}`, { ...opciones(), body: datos }),

  inhabilitarConductor: (id: number): Promise<{ id: number }> =>
    apiClient.delete<{ id: number }>(`/api/portal/conductores/${id}`, opciones()),

  /* ---------------------------------------------------------------- *
   * Despachos
   * ---------------------------------------------------------------- */

  despachos: (signal?: AbortSignal): Promise<{ total: number; despachos: DespachoPortal[] }> =>
    apiClient.get<{ total: number; despachos: DespachoPortal[] }>('/api/portal/despachos', {
      ...opciones(),
      signal,
    }),

  despachoEnVivo: (
    lado: number,
    transaccion: number,
    signal?: AbortSignal,
  ): Promise<{ despacho: DespachoEnVivoPortal }> =>
    apiClient.get<{ despacho: DespachoEnVivoPortal }>(
      `/api/portal/despachos/${lado}/${transaccion}/en-vivo`,
      { ...opciones(), signal },
    ),

  /* ---------------------------------------------------------------- *
   * Mensajes con la estación
   * ---------------------------------------------------------------- */

  mensajes: (
    signal?: AbortSignal,
  ): Promise<{ total: number; sin_leer: number; mensajes: MensajePortal[] }> =>
    apiClient.get<{ total: number; sin_leer: number; mensajes: MensajePortal[] }>(
      '/api/portal/mensajes',
      { ...opciones(), signal },
    ),

  enviarMensaje: (datos: MensajeFormulario): Promise<{ id: number }> =>
    apiClient.post<{ id: number }>('/api/portal/mensajes', { ...opciones(), body: datos }),

  marcarMensajeLeido: (id: number): Promise<{ id: number }> =>
    apiClient.patch<{ id: number }>(`/api/portal/mensajes/${id}/leido`, opciones()),
};
